/**
 * CRA3D Resume Studio — "New resume" picker.
 *
 * User chooses resume_type + starting_method. For `career_repath` we
 * also list their selected direction(s). Upload / paste starting
 * methods are visible but disabled with an honest "arriving with the
 * upload phase" label — per Mark's Phase 2 authorization the button
 * for a feature whose endpoint doesn't exist yet MUST be non-clickable.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  FileText, ArrowLeft, ArrowRight, Compass, Lock,
} from "lucide-react";

const RESUME_TYPES = [
  { id: "targeted_ats",               label: "Targeted (ATS-friendly)",
    body: "One resume tuned toward a specific role. Best default for online applications." },
  { id: "career_repath",              label: "Career Repath",
    body: "Framed around a selected CRA3D Lane of Direction — leans into transferable skills." },
  { id: "traditional_chronological",  label: "Chronological",
    body: "Classic reverse-chronological employment history." },
  { id: "skills_first",               label: "Skills-first",
    body: "Skills grouped at the top, employment history below." },
];

const STARTING_METHODS = [
  { id: "from_cra3d",   label: "Start from your approved CRA3D content",
    body: "Pre-populates sections with claims from your profile, directions, and portfolio. You still review each one before it lands on the resume.",
    disabled: false },
  { id: "from_scratch", label: "Start from a blank template",
    body: "Empty sections you fill in by typing. Great if you don't have an existing CRA3D profile yet.",
    disabled: false },
  { id: "upload_pdf",   label: "Upload an existing resume (PDF)",
    body: "You'll create the resume shell now and upload the PDF from the editor. 5MB max, no macros, no encrypted files.",
    disabled: false },
  { id: "upload_docx",  label: "Upload an existing resume (DOCX)",
    body: "You'll create the resume shell now and upload the DOCX from the editor. Macros / OLE objects / external links are rejected for safety.",
    disabled: false },
  { id: "paste_text",   label: "Paste resume text",
    body: "Paste up to 200 KB of plain text. You review each line before anything becomes a claim.",
    disabled: false },
];

export default function ResumeStudioNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDirection = searchParams.get("direction_id") || "";

  const [name, setName] = useState("");
  const [resumeType, setResumeType] = useState("targeted_ats");
  const [startingMethod, setStartingMethod] = useState("from_cra3d");
  const [directions, setDirections] = useState([]);
  const [linkedDirectionId, setLinkedDirectionId] = useState(preselectedDirection);
  const [status, setStatus] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/resume-studio/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/directions").then((r) => {
      const sets = r.data?.sets || [];
      const latest = sets[0];
      setDirections(latest?.directions || []);
    }).catch(() => {});
  }, []);

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError("Please give this resume a short name (e.g., \"Library Circulation — Springfield PL\").");
      return;
    }
    if (resumeType === "career_repath" && !linkedDirectionId) {
      setError("Choose one of your CRA3D Lanes of Direction to link this Career Repath resume to.");
      return;
    }
    setCreating(true);
    try {
      const body = {
        name: name.trim(),
        resume_type: resumeType,
        starting_method: startingMethod,
      };
      if (resumeType === "career_repath") body.linked_direction_id = linkedDirectionId;
      const { data } = await api.post("/resume-studio/resumes", body);
      toast.success("Resume created.");
      navigate(`/resume-studio/${data.resume.id}`);
    } catch (err) {
      setError(extractApiError(err, "Create failed."));
    } finally {
      setCreating(false);
    }
  };

  const consentPresent = !!status?.consent_on_file?.present;

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button
          onClick={() => navigate("/resume-studio")}
          data-testid="resume-studio-new-back"
          className="mb-4 text-xs text-fg-secondary hover:text-fg-primary flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to your resumes
        </button>

        <p className="text-xs uppercase tracking-wider text-fg-secondary">
          Resume Studio
        </p>
        <h1
          data-testid="resume-studio-new-title"
          className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary flex items-center gap-2"
        >
          <FileText className="h-6 w-6" aria-hidden="true" />
          New resume
        </h1>

        {!consentPresent && (
          <Alert
            variant="default"
            data-testid="resume-studio-new-consent-notice"
            className="mt-6 border-accent-warm/40 bg-accent-warm/5 text-fg-primary"
          >
            <AlertDescription>
              Consent is required before creating a resume. Go back and pick
              "New resume" from the list page to see the consent screen.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" data-testid="resume-studio-new-error"
                  className="mt-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-8 space-y-8">
          <section aria-labelledby="rs-name">
            <Label id="rs-name" className="text-sm font-medium text-fg-primary">
              Name this resume
            </Label>
            <p className="mt-1 text-xs text-fg-secondary">
              Something short so you can find it later. Not shown on the exported file.
            </p>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              placeholder='e.g., "Library Circulation — Springfield PL"'
              data-testid="resume-studio-new-name"
              className="mt-3 bg-bg-surface border-border-subtle text-fg-primary"
            />
          </section>

          <section aria-labelledby="rs-type">
            <p id="rs-type" className="text-sm font-medium text-fg-primary">
              Resume type
            </p>
            <p className="mt-1 text-xs text-fg-secondary">
              You can change this later.
            </p>
            <RadioGroup
              value={resumeType}
              onValueChange={setResumeType}
              className="mt-3 space-y-2"
            >
              {RESUME_TYPES.map((t) => (
                <label
                  key={t.id}
                  htmlFor={`type-${t.id}`}
                  data-testid={`resume-studio-new-type-${t.id}`}
                  className="flex items-start gap-3 rounded-md border border-border-subtle bg-bg-surface p-3 cursor-pointer hover:border-accent-warm/40"
                >
                  <RadioGroupItem value={t.id} id={`type-${t.id}`} className="mt-1" />
                  <span>
                    <span className="block text-sm font-medium text-fg-primary">{t.label}</span>
                    <span className="block text-xs text-fg-secondary mt-0.5">{t.body}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </section>

          {resumeType === "career_repath" && (
            <section aria-labelledby="rs-dir">
              <p id="rs-dir" className="text-sm font-medium text-fg-primary flex items-center gap-2">
                <Compass className="h-4 w-4" aria-hidden="true" />
                Which direction?
              </p>
              {directions.length === 0 ? (
                <p className="mt-2 text-xs text-fg-secondary">
                  You don't have direction options yet. Generate your Three Directions from Results, then come back.
                </p>
              ) : (
                <RadioGroup
                  value={linkedDirectionId}
                  onValueChange={setLinkedDirectionId}
                  className="mt-3 space-y-2"
                >
                  {directions.map((d) => (
                    <label
                      key={d.id}
                      htmlFor={`dir-${d.id}`}
                      data-testid={`resume-studio-new-direction-${d.id}`}
                      className="flex items-start gap-3 rounded-md border border-border-subtle bg-bg-surface p-3 cursor-pointer hover:border-accent-warm/40"
                    >
                      <RadioGroupItem value={d.id} id={`dir-${d.id}`} className="mt-1" />
                      <span>
                        <span className="block text-sm font-medium text-fg-primary">{d.title}</span>
                        {d.category && (
                          <span className="block text-xs text-fg-muted mt-0.5">
                            {d.category}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              )}
            </section>
          )}

          <section aria-labelledby="rs-start">
            <p id="rs-start" className="text-sm font-medium text-fg-primary">
              Starting point
            </p>
            <RadioGroup
              value={startingMethod}
              onValueChange={setStartingMethod}
              className="mt-3 space-y-2"
            >
              {STARTING_METHODS.map((m) => (
                <label
                  key={m.id}
                  htmlFor={`start-${m.id}`}
                  data-testid={`resume-studio-new-start-${m.id}`}
                  data-disabled={m.disabled}
                  className={
                    "flex items-start gap-3 rounded-md border p-3 " +
                    (m.disabled
                      ? "border-border-subtle/40 bg-bg-surface/40 text-fg-muted cursor-not-allowed"
                      : "border-border-subtle bg-bg-surface cursor-pointer hover:border-accent-warm/40")
                  }
                >
                  <RadioGroupItem
                    value={m.id}
                    id={`start-${m.id}`}
                    disabled={m.disabled}
                    className="mt-1"
                  />
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {m.label}
                      {m.disabled && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-fg-muted border border-border-subtle/50 rounded px-1.5 py-0.5">
                          <Lock className="h-3 w-3" aria-hidden="true" />
                          coming soon
                        </span>
                      )}
                    </span>
                    <span className="block text-xs mt-0.5">
                      {m.body}
                    </span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </section>

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/resume-studio")}
              data-testid="resume-studio-new-cancel-btn"
              className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={creating || !consentPresent}
              data-testid="resume-studio-new-create-btn"
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              {creating ? "Creating…" : "Create resume"}
              <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
